import { useMemo } from 'react';
import { CHART_POINTS } from '../components/charts';
import { downsample } from '../lib/chart';
import { windowSlice } from '../lib/derive';
import { useStore } from './store';

export interface WindowedSeries {
  /** Raw sample timestamps inside the window, oldest first. */
  times: number[];
  cpu: number[];
  /** MiB. */
  mem: number[];
  /** Bucket-averaged for drawing, with timestamps kept in step. */
  plotTimes: number[];
  plotCpu: number[];
  plotMem: number[];
  cpuCeiling: number;
  memCeiling: number;
  /** Seconds the window actually covers, which is less than the selected
   *  range until the tab has been open long enough to fill it. */
  coveredSeconds: number;
  /** True while the buffer is still shorter than the selected range. */
  partial: boolean;
  rangeSeconds: number;
}

/**
 * The chart-ready view of the sample rings for the currently selected range.
 * Windowing and downsampling both happen here so Overview and Metrics cannot
 * drift apart in what "the last 15 minutes" means.
 */
export function useWindowedSeries(): WindowedSeries {
  const range = useStore((s) => s.range);
  const interval = useStore((s) => s.statusIntervalMs);
  const times = useStore((s) => s.sampleTimes);
  const cpu = useStore((s) => s.sampleCpu);
  const mem = useStore((s) => s.sampleMem);
  const cpuCeiling = useStore((s) => s.cpuCeiling);
  const memCeiling = useStore((s) => s.memCeiling);

  return useMemo(() => {
    const wTimes = windowSlice(times, range, interval);
    const wCpu = windowSlice(cpu, range, interval);
    const wMem = windowSlice(mem, range, interval);

    const first = wTimes[0];
    const last = wTimes[wTimes.length - 1];
    const coveredSeconds =
      first !== undefined && last !== undefined && last > first
        ? Math.round((last - first) / 1000)
        : Math.max(0, wCpu.length - 1) * (interval / 1000);

    return {
      times: wTimes,
      cpu: wCpu,
      mem: wMem,
      plotTimes: downsample(wTimes, CHART_POINTS),
      plotCpu: downsample(wCpu, CHART_POINTS),
      plotMem: downsample(wMem, CHART_POINTS),
      cpuCeiling,
      memCeiling,
      coveredSeconds,
      partial: coveredSeconds < range - interval / 1000,
      rangeSeconds: range,
    };
  }, [times, cpu, mem, range, interval, cpuCeiling, memCeiling]);
}
